import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { History, X } from 'lucide-react';
import clsx from 'clsx';
import SearchInput from './SearchInput';
import Badge from './Badge';
import { DateInput, Select } from '@components/common';
import { auditService } from '@services';
import { NoteEntityType, AuditChange, AuditHistoryEntry } from '@app-types';
import { formatCalendarDate, utcToLocal } from '@utils/dateUtils';
import styles from './EntityHistory.module.css';

function toDateRangeError(dateFrom: string, dateTo: string): string {
  if (dateFrom && dateTo && dateFrom > dateTo) {
    return 'From date must be on or before To date';
  }
  return '';
}

const formatDate = (dateString: string) => {
  const date = utcToLocal(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatChangeValue = (value: string | null, type: string) => {
  if (value === null) return value;

  if (type === 'datetime') {
    try {
      return formatDate(value);
    } catch {
      return value;
    }
  }

  if (type === 'date') {
    return formatCalendarDate(value, value);
  }

  if (type === 'boolean') {
    const lowerValue = value.toLowerCase();
    if (lowerValue === 'true') return 'Yes';
    if (lowerValue === 'false') return 'No';
  }

  return value;
};

interface EntityHistoryProps {
  entityType: NoteEntityType;
  entityId: number;
}

/**
 * The History of a record: who changed what and when, with user, date and search filters.
 * Shared by the History tab of EntityActivity and the Contact-User History panel.
 */
const EntityHistory: React.FC<EntityHistoryProps> = ({ entityType, entityId }) => {
  const [selectedUser, setSelectedUser] = useState<string>('all');
  const [selectedField, setSelectedField] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');

  const { data: historyData, isLoading: isLoadingHistory } = useQuery({
    queryKey: ['audit-history', entityType, entityId],
    queryFn: () => auditService.getAuditHistory(entityType, entityId),
    enabled: !!entityId,
    staleTime: 30000, // Cache for 30 seconds
    // The app default is refetchOnMount: false, which would keep showing the cached list when the panel
    // is reopened after a save invalidated it (Bug #1170). Fresh data is still not refetched.
    refetchOnMount: true
  });

  // Get unique users from history data
  const uniqueUsers = useMemo(() => {
    if (!historyData) return [];
    const users = new Set<string>();
    historyData.forEach((entry) => {
      const userName = entry.user_name || 'System';
      users.add(userName);
    });
    return Array.from(users).sort((a, b) => a.localeCompare(b));
  }, [historyData]);

  // Get unique field names from history data
  const uniqueFields = useMemo(() => {
    if (!historyData) return [];
    const fields = new Set<string>();
    historyData.forEach((entry) => entry.changes?.forEach((change) => fields.add(change.field_name)));
    return Array.from(fields).sort((a, b) => a.localeCompare(b));
  }, [historyData]);

  // A refetch can drop the selected field (e.g. its only entry is gone); fall back to all fields instead of
  // leaving the user with an empty list and no hint why. The effect also clears the stored selection, so the
  // field does not silently re-apply if a later refetch brings it back.
  const activeField = selectedField === 'all' || uniqueFields.includes(selectedField) ? selectedField : 'all';
  useEffect(() => {
    if (historyData && activeField !== selectedField) setSelectedField(activeField);
  }, [historyData, activeField, selectedField]);

  // Filter history data
  const dateRangeError = toDateRangeError(dateFrom, dateTo);
  const filteredHistory = useMemo(() => {
    if (!historyData) return [];

    return historyData.filter((entry: AuditHistoryEntry) => {
      // Filter by user
      if (selectedUser !== 'all') {
        const userName = entry.user_name || 'System';
        if (userName !== selectedUser) return false;
      }

      // Filter by field: keeps whole entries that changed it, so Note-only entries drop out
      if (activeField !== 'all' && !entry.changes?.some((change) => change.field_name === activeField)) {
        return false;
      }

      // Skip date filtering while the range is inverted so results stay visible
      if (!dateRangeError) {
        const entryDate = new Date(entry.created_at);
        if (dateFrom) {
          const fromDate = new Date(dateFrom);
          fromDate.setHours(0, 0, 0, 0);
          if (entryDate < fromDate) return false;
        }
        if (dateTo) {
          const toDate = new Date(dateTo);
          toDate.setHours(23, 59, 59, 999);
          if (entryDate > toDate) return false;
        }
      }

      // Filter by search term
      if (searchTerm.trim()) {
        const search = searchTerm.toLowerCase();
        const matchesNotes = entry.notes?.toLowerCase().includes(search);
        const matchesChanges = entry.changes?.some(
          (change) =>
            change.field_name.toLowerCase().includes(search) ||
            change.old_value?.toLowerCase().includes(search) ||
            change.new_value?.toLowerCase().includes(search)
        );
        if (!matchesNotes && !matchesChanges) return false;
      }

      return true;
    });
  }, [historyData, selectedUser, activeField, dateFrom, dateTo, searchTerm, dateRangeError]);

  const clearFilters = () => {
    setSelectedUser('all');
    setSelectedField('all');
    setDateFrom('');
    setDateTo('');
    setSearchTerm('');
  };

  const hasActiveFilters = selectedUser !== 'all' || activeField !== 'all' || dateFrom || dateTo || searchTerm;

  // Render a single change in history
  const renderHistoryChange = (change: AuditChange) => {
    const isAddition = change.old_value === null;
    const isDeletion = change.new_value === null;
    const formattedOldValue = formatChangeValue(change.old_value, change.type);
    const formattedNewValue = formatChangeValue(change.new_value, change.type);

    // Determine which type of change to render
    let changeContent;
    if (isAddition) {
      changeContent = (
        <span className={clsx(styles.historyChangeValue, styles.historyChangeAdded)}>{formattedNewValue}</span>
      );
    } else if (isDeletion) {
      changeContent = (
        <span className={clsx(styles.historyChangeValue, styles.historyChangeRemoved)}>{formattedOldValue}</span>
      );
    } else {
      changeContent = (
        <>
          <span className={clsx(styles.historyChangeValue, styles.historyChangeOld)}>{formattedOldValue}</span>
          <span className={styles.historyChangeArrow}>→</span>
          <span className={clsx(styles.historyChangeValue, styles.historyChangeNew)}>{formattedNewValue}</span>
        </>
      );
    }

    return (
      <div key={`change-${change.field_name}-${change.old_value}-${change.new_value}`} className={styles.historyChange}>
        <span className={styles.historyChangeField}>{change.field_name}:</span>
        {changeContent}
      </div>
    );
  };

  // Render history empty state
  const renderHistoryEmpty = () => {
    if (historyData && historyData.length > 0) {
      return (
        <div className={styles.empty}>
          <History size={48} className={styles.emptyIcon} />
          <p>No results found</p>
          <p className={styles.emptySubtitle}>Try adjusting your filters</p>
        </div>
      );
    }

    return (
      <div className={styles.empty}>
        <History size={48} className={styles.emptyIcon} />
        <p>No history yet</p>
        <p className={styles.emptySubtitle}>Changes will appear here</p>
      </div>
    );
  };

  // Render history list content based on loading/data state
  const renderHistoryList = () => {
    if (isLoadingHistory) {
      return <div className={styles.loading}>Loading history...</div>;
    }

    if (filteredHistory.length > 0) {
      return (
        <div className={styles.historyTimeline}>
          {filteredHistory.map((entry) => (
            <div key={entry.id} className={styles.historyEntry} data-test-id="history-entry">
              <div className={styles.historyEntryMeta}>
                <div className={styles.historyEntryAuthor}>{entry.user_name || 'System'}</div>
                <div className={styles.historyEntryDate}>{formatDate(entry.created_at)}</div>
                {entry.action_type === 'created' && (
                  <Badge variant="success" className={styles.historyEntryAction} dataTestId="history-entry-created">
                    Created
                  </Badge>
                )}
              </div>
              <div className={styles.historyEntryContent}>
                {entry.changes && entry.changes.length > 0 && (
                  <div className={styles.historyChanges}>
                    {entry.changes.map((change) => renderHistoryChange(change))}
                  </div>
                )}
                {entry.notes && (
                  <div className={styles.historyEntryNotes}>
                    <div className={styles.historyEntryNotesLabel}>Notes:</div>
                    <div className={styles.historyEntryNotesContent}>{entry.notes}</div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      );
    }

    return renderHistoryEmpty();
  };

  return (
    <>
      {/* History filters */}
      <div className={styles.historyFilters}>
        <div className={styles.historyFiltersRow}>
          <div className={styles.historyFilterGroup}>
            <Select
              id="user-filter"
              label="User:"
              value={selectedUser}
              onChange={(value) => setSelectedUser(value)}
              options={[
                { value: 'all', label: 'All Users' },
                ...uniqueUsers.map((user) => ({ value: user, label: user }))
              ]}
              dataTestId="user-filter"
            />
          </div>

          <div className={styles.historyFilterGroup}>
            <Select
              id="field-filter"
              label="Field:"
              value={activeField}
              onChange={(value) => setSelectedField(value)}
              options={[
                { value: 'all', label: 'All Fields' },
                ...uniqueFields.map((field) => ({ value: field, label: field }))
              ]}
              dataTestId="field-filter"
            />
          </div>

          <div className={styles.historyFilterGroup}>
            <DateInput
              id="date-from"
              label="From:"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              max={dateTo || undefined}
              dataTestId="date-from"
              wrapperClassName={styles.historyFilterDateWrapper}
            />
          </div>

          <div className={styles.historyFilterGroup}>
            <DateInput
              id="date-to"
              label="To:"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              min={dateFrom || undefined}
              error={dateRangeError || undefined}
              dataTestId="date-to"
              wrapperClassName={styles.historyFilterDateWrapper}
            />
          </div>

          <SearchInput
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search by field name or values..."
            className={styles.historyFilterSearch}
          />

          <button
            onClick={clearFilters}
            className={styles.historyFilterClear}
            title="Clear all filters"
            disabled={!hasActiveFilters}
          >
            <X size={16} />
            Clear
          </button>
        </div>
      </div>

      {/* History list */}
      <div className={styles.historyList} data-test-id="history-list">
        {renderHistoryList()}
      </div>
    </>
  );
};

export default EntityHistory;
